import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes } from 'react';
import { useId } from 'react';

import { cn } from '@/shared/utils/cn';

interface FieldChrome {
  label: string;
  error?: string | undefined;
  /**
   * A line of help under the field.
   *
   * It hangs off `aria-describedby` and **not** off a `<label>` wrapped around the
   * input: a screen reader reads the whole label as the field's name, so a hint inside
   * one turns "Código de cliente" into "Código de cliente El del ERP. Es la clave: no se
   * repite." every time the focus lands there.
   */
  hint?: ReactNode | undefined;
  /** Shown after the label, quietly: «opcional». */
  optional?: boolean;
}

function useFieldIds(id: string | undefined, error: string | undefined, hint: ReactNode) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const errorId = `${inputId}-error`;
  const hintId = `${inputId}-hint`;
  const describedBy = [error ? errorId : null, hint ? hintId : null].filter(Boolean).join(' ');
  return { inputId, errorId, hintId, describedBy: describedBy || undefined };
}

function Label({
  htmlFor,
  optional,
  children,
}: {
  htmlFor: string;
  optional?: boolean | undefined;
  children: ReactNode;
}) {
  return (
    <label htmlFor={htmlFor} className="text-ink-2 text-[13.5px] font-medium">
      {children}
      {optional ? <span className="text-ink-4 ml-1.5 font-normal">opcional</span> : null}
    </label>
  );
}

function Below({
  hint,
  hintId,
  error,
  errorId,
}: {
  hint: ReactNode;
  hintId: string;
  error: string | undefined;
  errorId: string;
}) {
  return (
    <>
      {hint ? (
        <p id={hintId} className="text-ink-3 text-[12.5px] leading-snug">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} role="alert" className="text-danger text-[13px]">
          {error}
        </p>
      ) : null}
    </>
  );
}

type InputProps = InputHTMLAttributes<HTMLInputElement> & FieldChrome;

export function Input({ label, error, hint, optional, className, id, ...props }: InputProps) {
  const ids = useFieldIds(id, error, hint);
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={ids.inputId} optional={optional}>
        {label}
      </Label>
      <input
        id={ids.inputId}
        aria-invalid={error ? true : undefined}
        aria-describedby={ids.describedBy}
        className={cn('field', className)}
        {...props}
      />
      <Below hint={hint} hintId={ids.hintId} error={error} errorId={ids.errorId} />
    </div>
  );
}

type SelectProps = SelectHTMLAttributes<HTMLSelectElement> & FieldChrome;

/** A `<select>` with the same chrome as `Input`: label above, hint below. */
export function Select({
  label,
  error,
  hint,
  optional,
  className,
  id,
  children,
  ...props
}: SelectProps) {
  const ids = useFieldIds(id, error, hint);
  return (
    <div className="flex flex-col gap-1">
      <Label htmlFor={ids.inputId} optional={optional}>
        {label}
      </Label>
      <select
        id={ids.inputId}
        aria-invalid={error ? true : undefined}
        aria-describedby={ids.describedBy}
        className={cn('field', className)}
        {...props}
      >
        {children}
      </select>
      <Below hint={hint} hintId={ids.hintId} error={error} errorId={ids.errorId} />
    </div>
  );
}
